import {
  useMemo,
  useEffect,
  useState,
  JSX,
  type SetStateAction,
  type Dispatch,
} from "react";
import { Heading } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { msg, Trans } from "@lingui/macro";
import { FilterList, Item as FilterItem } from "src/atoms/FilterList";
import { BrandCarousel } from "src/molecules/BrandCarousel";
import styles from "./ExploreMoreCarousel.module.scss";
import { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import type { CarouselData, SupportedKeys } from "src/types/Carousels";
import { SEGMENT_PROMOTION } from "src/utils/constants";
import { SEGMENT_PROMOTION_TYPE } from "src/segment";

type ExploreMoreCarouselProps = {
  genderId: string;
  hideTitle?: boolean;
  shopType: string;
  activeSection: SupportedKeys;
  carouselData: CarouselData[];
  setActiveSection: Dispatch<SetStateAction<SupportedKeys>>;
};

const translatedTitles: Record<SupportedKeys, MessageDescriptor> = {
  ["popular_shops"]: msg`Most popular`,
  ["favourited_shops"]: msg`For you`,
  ["department_designer"]: msg`Designer`,
  ["just_arrived_shops"]: msg`New Items`,
  ["conscious_shops"]: msg`Ethically rated`,
};

const SEGMENT_PROMOTION_MAP: Record<SupportedKeys, SEGMENT_PROMOTION_TYPE> = {
  popular_shops: SEGMENT_PROMOTION_TYPE.popularShops,
  favourited_shops: SEGMENT_PROMOTION_TYPE.favouritedShops,
  department_designer: SEGMENT_PROMOTION_TYPE.departmentDesigner,
  just_arrived_shops: SEGMENT_PROMOTION_TYPE.justArrivedShops,
  conscious_shops: SEGMENT_PROMOTION_TYPE.consciousShops,
};

function ExploreMoreCarousel({
  carouselData,
  genderId,
  hideTitle,
  shopType,
  activeSection,
  setActiveSection,
}: ExploreMoreCarouselProps): JSX.Element | null {
  const { _ } = useLingui();
  const { isLoggedIn } = useNewIsLoggedIn();
  const [currentUrl, setCurrentUrl] = useState("");
  const [open, setOpen] = useState<boolean>(false);

  const filterData: FilterItem[] = useMemo(() => {
    return carouselData.map((item) => {
      if (!isLoggedIn && item.type === "favourited_shops") {
        return {
          key: item.type,
          content: _(msg`Log in to access`),
          active: item.type === activeSection,
          onClick: (key) => {
            setOpen(true);
            setActiveSection(key as SupportedKeys);
          },
        };
      }

      return {
        key: item.type,
        content: _(translatedTitles[item.type]),
        active: item.type === activeSection,
        onClick: (key) => setActiveSection(key as SupportedKeys),
      };
    });
  }, [carouselData, activeSection, _, isLoggedIn, setActiveSection]);

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  useEffect(() => {
    const [item] = carouselData;
    setActiveSection((section) => section || item?.type || "");
  }, [carouselData, setActiveSection]);

  const currentSection = carouselData.find(
    (item) => item.type === activeSection
  );

  if (!currentSection) return null;

  return (
    <section className={styles.section}>
      <div>
        {!hideTitle && (
          <Heading
            as="h3"
            fontFamily="PlusJakartaSans"
            fontSize={[2, 2, 2, 3]}
            mb={3}
            color="Black"
            sx={{
              lineHeight: 1.25,
              textTransform: "none",
            }}
          >
            <Trans>Explore more</Trans>
          </Heading>
        )}

        <Box mb={4} sx={{ position: "relative", zIndex: 2 }}>
          <FilterList items={filterData} />
        </Box>
      </div>

      <div className={styles.carousel}>
        <BrandCarousel
          key={currentSection.type} // key added intentionally. Forces remounting on the component on activeSection change
          genderId={genderId}
          brands={currentSection.data}
          // Match the uploaded store-visual aspect ratios so the banner is shown
          // in full without cropping: desktop 2240x1000 (~0.45), mobile 550x500 (~0.91).
          cardRatio={{ mobile: 0.91, desktop: 0.45 }}
          slidesDesktop={4}
          slidesTablet={3}
          slidesMobile={1.5}
          tab={activeSection}
          promotionType={SEGMENT_PROMOTION_MAP[activeSection]}
          pageName="homepage"
          carouselId={SEGMENT_PROMOTION[activeSection]}
          carouselPosition={1}
          title={_(translatedTitles[activeSection])}
          shopType={shopType}
        />
      </div>
      {open && (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={currentUrl}
          trackingData={{
            pageName: "homepage",
            shopType: genderId,
            actionSource: "feature_brand_tab",
            redirectTo: currentUrl,
          }}
        />
      )}
    </section>
  );
}

export default ExploreMoreCarousel;
